import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, CheckCircle, XCircle, Loader2, Users, Building2, Heart, Shield, Award, Sparkles, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { markSlideViewed, markQuizCompleted, getActivityProgress } from '../utils/progressTracker';
import confetti from 'canvas-confetti';

const ACTIVITY_ID = 'deap-activite11';

const DEAPActivite11Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [showCongrats, setShowCongrats] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const savedProgress = getActivityProgress(ACTIVITY_ID);
    if (savedProgress && savedProgress.lastViewedSlide > 0) {
      // Resume optionally
    }
  }, []);

  useEffect(() => {
    if (showCongrats) {
      const duration = 3 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 1000 };
      const randomInRange = (min, max) => Math.random() * (max - min) + min;
      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) return clearInterval(interval);
        const particleCount = 50 * (timeLeft / duration);
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
        confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
      }, 250);
    }
  }, [showCongrats]);

  const slides = [
    // Slide 1 - Titre
    {
      id: 1,
      type: 'title',
      title: "Activité 11",
      subtitle: "Organisation de son activité au sein d'une équipe pluri-professionnelle",
      description: "Garantir la qualité d'accueil et la gestion des risques - En structure petite enfance et en milieu hospitalier"
    },
    // Slide 2 - Sommaire
    {
      id: 2,
      type: 'sommaire',
      title: "Sommaire",
      sections: [
        { num: "1", title: "Introduction : l'organisation, pilier de la qualité" },
        { num: "2", title: "Les professionnels et leurs missions" },
        { num: "3", title: "Organisation en structure petite enfance" },
        { num: "4", title: "Organisation en milieu hospitalier" },
        { num: "5", title: "Gestion des imprévus" },
        { num: "6", title: "Gestion des risques" },
        { num: "7", title: "Outils de coordination et d'amélioration" },
        { num: "8", title: "Synthèse comparative EAJE / Hôpital" },
        { num: "9", title: "Conclusion générale" }
      ]
    },
    // SECTION 1 - Introduction
    {
      id: 3,
      type: 'section',
      section: "1",
      title: "Introduction : l'organisation, pilier de la qualité et de la sécurité"
    },
    {
      id: 4,
      type: 'content',
      title: "Pourquoi l'organisation est essentielle",
      content: "L'organisation professionnelle est essentielle dans toute structure accueillant des enfants, qu'il s'agisse d'un EAJE ou d'un service hospitalier.",
      subtitle: "Elle permet de :",
      bullets: [
        "Garantir la sécurité physique et affective",
        "Assurer la continuité des soins",
        "Maintenir une cohérence éducative ou thérapeutique",
        "Respecter le cadre réglementaire",
        "Prévenir les risques",
        "Favoriser la qualité de l'accompagnement"
      ]
    },
    {
      id: 5,
      type: 'highlight',
      title: "Le travail en équipe pluri-professionnelle",
      content: "Il repose sur :",
      items: [
        { icon: "🔗", text: "Une coordination structurée" },
        { icon: "📝", text: "Des transmissions fiables" },
        { icon: "👥", text: "Une définition claire des rôles" }
      ]
    },
    // SECTION 2 - Professionnels
    {
      id: 6,
      type: 'section',
      section: "2",
      title: "Les professionnels et leurs missions"
    },
    {
      id: 7,
      type: 'subsection',
      title: "2.1 En structure petite enfance"
    },
    {
      id: 8,
      type: 'roleCard',
      title: "Le CAP AEPE",
      color: "#EC4899",
      missions: [
        "Accompagne l'enfant dans les actes de la vie quotidienne",
        "Participe aux soins d'hygiène",
        "Accompagne les repas et le sommeil",
        "Propose des activités d'éveil",
        "Assure l'entretien des espaces"
      ]
    },
    {
      id: 9,
      type: 'roleCard',
      title: "Le DEAP (Auxiliaire de Puériculture)",
      color: "#8B5CF6",
      missions: [
        "Réalise les soins d'hygiène et de confort",
        "Observe l'état de santé",
        "Repère les signes d'alerte",
        "Participe à la prévention des risques",
        "Contribue au projet éducatif"
      ]
    },
    {
      id: 10,
      type: 'roleCard',
      title: "Le DEEJE (Éducateur de Jeunes Enfants)",
      color: "#10B981",
      missions: [
        "Élabore et met en œuvre le projet pédagogique",
        "Accompagne le développement global",
        "Anime la réflexion éducative",
        "Conduit des analyses de pratique"
      ]
    },
    {
      id: 11,
      type: 'twoRoles',
      roles: [
        {
          title: "L'IDE en EAJE",
          color: "#3B82F6",
          missions: ["Met en œuvre les protocoles médicaux", "Assure la prévention sanitaire", "Accompagne les équipes sur les questions de santé"]
        },
        {
          title: "Le Référent Santé",
          color: "#F59E0B",
          missions: ["Conseille l'équipe en matière de santé", "Met en place les protocoles d'urgence", "Favorise l'inclusion des enfants en situation de handicap"]
        }
      ]
    },
    {
      id: 12,
      type: 'subsection',
      title: "2.2 En milieu hospitalier"
    },
    {
      id: 13,
      type: 'imageSlide',
      title: "L'équipe hospitalière",
      image: "/images/reunion_equipe.png",
      imageAlt: "Réunion d'équipe médicale",
      bullets: [
        "IDE : Soins techniques, traitements, surveillance, coordination",
        "Auxiliaire de puériculture : Soins d'hygiène, observation, transmissions",
        "Puéricultrice : Coordination des soins pédiatriques, accompagnement familles",
        "Sage-femme : Suivi grossesse, accouchement, nouveau-né",
        "Pédiatre : Diagnostics, prescriptions, orientations médicales"
      ]
    },
    // SECTION 3 - Organisation EAJE
    {
      id: 14,
      type: 'section',
      section: "3",
      title: "Organisation en structure petite enfance"
    },
    {
      id: 15,
      type: 'regulation',
      title: "3.1 Cadre réglementaire",
      content: "Le Code de la santé publique impose :",
      rules: [
        { ratio: "1 pour 5", desc: "enfants qui ne marchent pas", icon: "👶" },
        { ratio: "1 pour 8", desc: "enfants qui marchent", icon: "🚶" }
      ],
      note: "Le respect des taux d'encadrement garantit la sécurité et la qualité d'accompagnement."
    },
    {
      id: 16,
      type: 'daySchedule',
      title: "3.2 Organisation d'une journée type",
      periods: [
        {
          icon: "🌅",
          name: "Ouverture",
          tasks: ["Lecture des transmissions", "Préparation des espaces", "Répartition des rôles"]
        },
        {
          icon: "🧸",
          name: "Journée",
          tasks: ["Activités d'éveil adaptées", "Soins d'hygiène", "Repas sécurisés", "Temps de repos surveillé"]
        },
        {
          icon: "🌆",
          name: "Clôture",
          tasks: ["Transmissions aux familles", "Désinfection", "Transmission écrite à l'équipe suivante"]
        }
      ]
    },
    // SECTION 4 - Organisation Hôpital
    {
      id: 17,
      type: 'section',
      section: "4",
      title: "Organisation en milieu hospitalier"
    },
    {
      id: 18,
      type: 'hierarchy',
      title: "4.1 Cadre hiérarchique",
      content: "L'organisation hospitalière repose sur :",
      levels: [
        { name: "Direction", color: "#DC2626" },
        { name: "Chef de service", color: "#F59E0B" },
        { name: "Cadre de santé", color: "#10B981" },
        { name: "Équipe médicale", color: "#3B82F6" },
        { name: "Équipe paramédicale", color: "#8B5CF6" }
      ],
      note: "Chaque professionnel agit dans son champ de compétences défini légalement."
    },
    {
      id: 19,
      type: 'daySchedule',
      title: "4.2 Organisation d'une journée type",
      periods: [
        {
          icon: "🌅",
          name: "Prise de poste",
          tasks: ["Transmission orale", "Lecture des dossiers patients", "Répartition des chambres", "Vérification du matériel"]
        },
        {
          icon: "🩺",
          name: "Soins",
          tasks: ["Soins d'hygiène", "Soins techniques", "Administration des traitements", "Surveillance clinique"]
        },
        {
          icon: "📋",
          name: "Staff médical",
          tasks: ["Analyse de l'évolution des patients", "Ajustement des traitements", "Coordination des interventions"]
        }
      ],
      note: "Chaque acte est tracé dans le dossier patient."
    },
    // SECTION 5 - Imprévus
    {
      id: 20,
      type: 'section',
      section: "5",
      title: "Gestion des imprévus"
    },
    {
      id: 21,
      type: 'warning',
      title: "Types d'imprévus",
      content: "En EAJE comme à l'hôpital, les imprévus peuvent concerner :",
      bullets: [
        "L'absence d'un professionnel",
        "L'aggravation de l'état d'un enfant",
        "Un accident",
        "Une situation d'urgence"
      ],
      warningColor: "#F59E0B"
    },
    {
      id: 22,
      type: 'content',
      title: "L'adaptation repose sur",
      bullets: [
        "La communication immédiate",
        "La hiérarchisation des priorités",
        "L'application des protocoles",
        "Le travail coordonné"
      ],
      highlight: "La sécurité reste toujours la priorité absolue."
    },
    // SECTION 6 - Risques
    {
      id: 23,
      type: 'section',
      section: "6",
      title: "Gestion des risques"
    },
    {
      id: 24,
      type: 'twoColumns',
      title: "Risques par contexte",
      column1: {
        title: "🏠 En EAJE",
        items: ["Chutes", "Infections", "Erreurs médicamenteuses", "TMS (troubles musculo-squelettiques)", "Risques psychosociaux"]
      },
      column2: {
        title: "🏥 En milieu hospitalier",
        items: ["Erreurs de traitement", "Infections nosocomiales", "Urgences vitales", "Risques professionnels"]
      }
    },
    {
      id: 25,
      type: 'content',
      title: "La prévention s'appuie sur",
      bullets: [
        "Protocoles écrits",
        "Double vérification",
        "Hygiène rigoureuse",
        "Formations régulières",
        "Analyse des événements indésirables"
      ]
    },
    // SECTION 7 - Outils
    {
      id: 26,
      type: 'section',
      section: "7",
      title: "Outils de coordination et d'amélioration continue"
    },
    {
      id: 27,
      type: 'imageSlide',
      title: "Outils communs aux deux contextes",
      image: "/images/travail_equipe.png",
      imageAlt: "Travail d'équipe - mains réunies",
      bullets: [
        "Réunions d'équipe régulières",
        "Analyse de la pratique professionnelle",
        "Journées pédagogiques",
        "Formation continue",
        "Réorganisation des espaces si nécessaire"
      ],
      note: "Ces outils favorisent la cohésion, la réflexion et la prévention de l'usure professionnelle."
    },
    // SECTION 8 - Synthèse
    {
      id: 28,
      type: 'section',
      section: "8",
      title: "Synthèse comparative EAJE / Hôpital"
    },
    {
      id: 29,
      type: 'comparison',
      title: "Comparaison des deux contextes",
      headers: ["Critère", "Structure petite enfance", "Milieu hospitalier"],
      rows: [
        ["Projet", "Projet éducatif", "Projet de soins"],
        ["Cadre", "Taux d'encadrement réglementé", "Organisation hiérarchisée"],
        ["Transmissions", "Éducatives et sanitaires", "Médicalisées"],
        ["Risques", "Prévention des risques courants", "Gestion des urgences vitales"],
        ["Objectif", "Sécurité et développement", "Sécurité et traitement"]
      ]
    },
    // SECTION 9 - Conclusion
    {
      id: 30,
      type: 'section',
      section: "9",
      title: "Conclusion générale"
    },
    {
      id: 31,
      type: 'conclusion',
      title: "L'organisation : un levier majeur de qualité",
      content: "L'organisation au sein d'une équipe pluri-professionnelle constitue un levier majeur de qualité, que ce soit en EAJE ou en milieu hospitalier.",
      bullets: [
        "Une répartition claire des rôles",
        "Une communication structurée",
        "Le respect du cadre légal",
        "La gestion des risques",
        "L'adaptabilité face aux imprévus"
      ],
      finalNote: "Elle garantit la sécurité, la continuité et la cohérence de la prise en charge de l'enfant."
    }
  ];

  const quizQuestions = [
    { id: 1, question: "Quel est l'objectif principal du travail en équipe pluri-professionnelle en crèche ?", options: ["Se répartir les tâches", "Garantir la sécurité et la qualité d'accueil", "Diminuer la charge de travail", "Remplacer la direction"], correct: 1 },
    { id: 2, question: "Quel est le taux d'encadrement réglementaire pour des enfants qui ne marchent pas ?", options: ["1 pour 4", "1 pour 5", "1 pour 6", "1 pour 8"], correct: 1 },
    { id: 3, question: "Quel est le taux d'encadrement pour des enfants qui marchent ?", options: ["1 pour 6", "1 pour 7", "1 pour 8", "1 pour 10"], correct: 2 },
    { id: 4, question: "Le DEAP est particulièrement compétent pour :", options: ["Diriger la structure", "Élaborer le budget", "Observer l'état de santé de l'enfant", "Faire l'entretien des locaux uniquement"], correct: 2 },
    { id: 5, question: "Le DEEJE a pour mission principale :", options: ["Les soins techniques", "L'élaboration du projet pédagogique", "La gestion administrative", "Le nettoyage"], correct: 1 },
    { id: 6, question: "Le référent santé a pour rôle :", options: ["Remplacer tous les professionnels absents", "Mettre en place les protocoles de santé", "Gérer les plannings", "Organiser les fêtes"], correct: 1 },
    { id: 7, question: "Les transmissions servent à :", options: ["Critiquer les collègues", "Partager des informations utiles sur l'enfant", "Remplir un document obligatoire sans intérêt", "Évaluer les parents"], correct: 1 },
    { id: 8, question: "L'analyse de la pratique permet :", options: ["De sanctionner les erreurs", "De prendre du recul sur les situations professionnelles", "De modifier les contrats", "D'évaluer les enfants"], correct: 1 },
    { id: 9, question: "En cas d'absence imprévue d'un collègue, la priorité est :", options: ["Annuler toutes les activités", "Prioriser la sécurité des enfants", "Continuer comme si de rien n'était", "Fermer la section"], correct: 1 },
    { id: 10, question: "Quel est un risque fréquent en crèche ?", options: ["Accident de ski", "Chute", "Accident de la route", "Noyade en mer"], correct: 1 },
    { id: 11, question: "Les TMS concernent :", options: ["Les enfants uniquement", "Les troubles musculo-squelettiques des professionnels", "Les maladies infantiles", "Les infections virales"], correct: 1 },
    { id: 12, question: "Une organisation efficace doit être :", options: ["Rigide", "Flexible", "Autoritaire", "Improvisée"], correct: 1 },
    { id: 13, question: "La réunion d'équipe permet :", options: ["D'organiser les vacances", "De coordonner les pratiques professionnelles", "De sanctionner", "De remplacer les transmissions"], correct: 1 },
    { id: 14, question: "L'accueil du matin a pour objectif principal :", options: ["Gagner du temps", "Sécuriser l'enfant et recueillir les informations importantes", "Vérifier les sacs", "Distribuer les repas"], correct: 1 },
    { id: 15, question: "En cas de fièvre chez un enfant, on :", options: ["Ignore la situation", "Applique le protocole et informe la direction", "Donne un médicament sans autorisation", "Attend la fin de journée"], correct: 1 },
    { id: 16, question: "La gestion des risques vise à :", options: ["Éviter toute activité", "Identifier, prévenir et limiter les dangers", "Multiplier les règles", "Restreindre l'autonomie"], correct: 1 },
    { id: 17, question: "Une bonne organisation favorise :", options: ["Le stress", "La cohérence éducative", "Les conflits", "L'improvisation permanente"], correct: 1 },
    { id: 18, question: "La puéricultrice en structure a un rôle :", options: ["Exclusivement administratif", "De coordination des soins et expertise santé", "D'animation uniquement", "De surveillance des parents"], correct: 1 },
    { id: 19, question: "Une action d'amélioration qualité peut être :", options: ["Supprimer les réunions", "Mettre en place un tableau clair des responsabilités", "Réduire le personnel", "Ignorer les incidents"], correct: 1 },
    { id: 20, question: "L'objectif principal de votre organisation quotidienne est :", options: ["Finir plus tôt", "Garantir la sécurité, le bien-être et la qualité d'accueil", "Faire plaisir à la direction", "Respecter uniquement le planning"], correct: 1 }
  ];

  const API_URL = window.location.origin || '';

  const transformTextForTTS = (text) => {
    if (!text) return '';
    return text
      .replace(/\bDEAP\b/g, 'diplôme d\'état d\'auxiliaire de puériculture')
      .replace(/\bDEEJE\b/g, 'diplôme d\'état d\'éducateur de jeunes enfants')
      .replace(/\bEAJE\b/g, 'établissement d\'accueil du jeune enfant')
      .replace(/\bIDE\b/g, 'infirmière diplômée d\'état')
      .replace(/\bTMS\b/g, 'troubles musculo-squelettiques')
      .replace(/\bCAP AEPE\b/g, 'CAP accompagnant éducatif petite enfance')
      .replace(/\bEJE\b/g, 'éducateur de jeunes enfants');
  };

  const speakText = async (text) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const transformedText = transformTextForTTS(text).substring(0, 800);
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: transformedText, voice: 'nova', speed: 1.1 })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.audio_url || data.audio_base64) {
          if (audioRef.current) audioRef.current.pause();
          audioRef.current = new Audio(data.audio_url || `data:audio/mp3;base64,${data.audio_base64}`);
          audioRef.current.play();
          setIsPlaying(true);
          audioRef.current.onended = () => setIsPlaying(false);
        }
      } else {
        toast.error("Erreur lors de la génération audio");
      }
    } catch (error) {
      toast.error("Erreur de connexion au service audio");
    } finally {
      setIsLoading(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const getSlideText = (slide) => {
    let text = slide.title || '';
    if (slide.subtitle) text += '. ' + slide.subtitle;
    if (slide.content) text += '. ' + slide.content;
    if (slide.bullets) text += '. ' + slide.bullets.join('. ');
    if (slide.note) text += '. ' + slide.note;
    if (slide.missions) text += '. ' + slide.missions.join('. ');
    return text;
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      const newSlide = currentSlide + 1;
      setCurrentSlide(newSlide);
      markSlideViewed(ACTIVITY_ID, newSlide, slides.length);
    } else {
      setShowQuiz(true);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) setCurrentSlide(currentSlide - 1);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    markSlideViewed(ACTIVITY_ID, index, slides.length);
  };

  const handleQuizAnswer = (questionId, answerIndex) => {
    if (!quizSubmitted) setQuizAnswers({ ...quizAnswers, [questionId]: answerIndex });
  };

  const submitQuiz = () => {
    let score = 0;
    quizQuestions.forEach(q => { if (quizAnswers[q.id] === q.correct) score++; });
    setQuizScore(score);
    setQuizSubmitted(true);
    markQuizCompleted(ACTIVITY_ID, score, quizQuestions.length);
    if (score >= 16) setShowCongrats(true);
    toast.success(`Quiz terminé ! Score: ${score}/${quizQuestions.length}`);
  };

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center p-8" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)' }}>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">{slide.title}</h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl">{slide.subtitle}</p>
            <p className="text-lg text-white/80 mt-4">{slide.description}</p>
          </div>
        );

      case 'sommaire':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-w-4xl mx-auto">
              {slide.sections.map((section, idx) => (
                <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded-lg shadow-sm border border-purple-100 cursor-pointer hover:bg-purple-50 transition-colors" onClick={() => goToSlide(Math.min(idx * 3 + 2, slides.length - 1))}>
                  <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{section.num}</span>
                  <span className="text-gray-700 text-xs">{section.title}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)' }}>
            <div className="text-6xl font-bold text-white/30 mb-4">{slide.section}</div>
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center px-8">{slide.title}</h2>
          </div>
        );

      case 'subsection':
        return (
          <div className="flex flex-col items-center justify-center h-full" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #C084FC 100%)' }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white text-center px-8">{slide.title}</h2>
          </div>
        );

      case 'content':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.subtitle && <p className="text-purple-700 font-medium mb-3">{slide.subtitle}</p>}
            {slide.bullets && (
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-purple-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
            {slide.highlight && (
              <div className="mt-4 p-4 bg-purple-100 border-l-4 border-purple-500 rounded">
                <p className="text-purple-800 font-medium">{slide.highlight}</p>
              </div>
            )}
          </div>
        );

      case 'highlight':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4">{slide.title}</h2>
            {slide.content && <p className="text-gray-700 mb-6">{slide.content}</p>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {slide.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm border border-purple-100">
                  <span className="text-3xl">{item.icon}</span>
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'roleCard':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <div className="max-w-2xl mx-auto">
              <div className="rounded-xl overflow-hidden shadow-lg" style={{ borderTop: `4px solid ${slide.color}` }}>
                <div className="p-6 bg-white">
                  <h2 className="text-xl font-bold mb-4" style={{ color: slide.color }}>{slide.title}</h2>
                  <ul className="space-y-2">
                    {slide.missions.map((mission, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle size={18} style={{ color: slide.color }} className="flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{mission}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'twoRoles':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {slide.roles.map((role, idx) => (
                <div key={idx} className="rounded-xl overflow-hidden shadow-lg" style={{ borderTop: `4px solid ${role.color}` }}>
                  <div className="p-4 bg-white">
                    <h3 className="text-lg font-bold mb-3" style={{ color: role.color }}>{role.title}</h3>
                    <ul className="space-y-2">
                      {role.missions.map((m, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle size={16} style={{ color: role.color }} className="flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{m}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'imageSlide':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-3">{slide.title}</h2>
            <div className="flex flex-col md:flex-row gap-4 items-start">
              <div className="md:w-1/2">
                <img src={slide.image} alt={slide.imageAlt} className="w-full max-h-56 object-cover rounded-lg shadow-md" />
              </div>
              <div className="md:w-1/2">
                {slide.bullets && (
                  <ul className="space-y-2">
                    {slide.bullets.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle size={16} className="text-purple-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {slide.note && <p className="mt-3 text-sm text-purple-600 italic bg-purple-50 p-3 rounded">{slide.note}</p>}
          </div>
        );

      case 'regulation':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4">{slide.title}</h2>
            <p className="text-gray-700 mb-6">{slide.content}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {slide.rules.map((rule, idx) => (
                <div key={idx} className="p-4 bg-white rounded-lg shadow-md border-l-4 border-purple-500 flex items-center gap-4">
                  <span className="text-3xl">{rule.icon}</span>
                  <div>
                    <p className="text-2xl font-bold text-purple-700">{rule.ratio}</p>
                    <p className="text-gray-600 text-sm">{rule.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-purple-600 italic text-center">{slide.note}</p>
          </div>
        );

      case 'daySchedule':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-xl font-bold text-purple-800 mb-4">{slide.title}</h2>
            <div className="space-y-3">
              {slide.periods.map((period, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow-sm p-3 border-l-4 border-purple-400">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{period.icon}</span>
                    <h3 className="font-bold text-purple-700">{period.name}</h3>
                  </div>
                  <ul className="grid grid-cols-2 gap-1">
                    {period.tasks.map((task, i) => (
                      <li key={i} className="text-gray-600 text-sm flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-purple-400 rounded-full"></span>
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {slide.note && <p className="mt-3 text-sm text-purple-600 italic">{slide.note}</p>}
          </div>
        );

      case 'hierarchy':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-4">{slide.title}</h2>
            <p className="text-gray-700 mb-6">{slide.content}</p>
            <div className="flex flex-col items-center gap-2">
              {slide.levels.map((level, idx) => (
                <div key={idx} className="flex items-center gap-2" style={{ width: `${100 - idx * 10}%` }}>
                  <div className="flex-1 p-3 rounded-lg text-white text-center font-medium" style={{ backgroundColor: level.color }}>
                    {level.name}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm text-purple-600 italic text-center">{slide.note}</p>
          </div>
        );

      case 'warning':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: `linear-gradient(180deg, ${slide.warningColor}10 0%, #FFFFFF 100%)` }}>
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={28} style={{ color: slide.warningColor }} />
              <h2 className="text-2xl font-bold" style={{ color: slide.warningColor }}>{slide.title}</h2>
            </div>
            {slide.content && <p className="text-gray-700 mb-4">{slide.content}</p>}
            {slide.bullets && (
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <XCircle size={18} style={{ color: slide.warningColor }} className="flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{bullet}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );

      case 'twoColumns':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-2xl font-bold text-purple-800 mb-6 text-center">{slide.title}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-pink-50 rounded-lg p-4 border border-pink-200">
                <h3 className="font-bold text-pink-700 mb-3">{slide.column1.title}</h3>
                <ul className="space-y-2">
                  {slide.column1.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700 text-sm">
                      <span className="w-2 h-2 bg-pink-400 rounded-full"></span> {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h3 className="font-bold text-blue-700 mb-3">{slide.column2.title}</h3>
                <ul className="space-y-2">
                  {slide.column2.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-gray-700 text-sm">
                      <span className="w-2 h-2 bg-blue-400 rounded-full"></span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );

      case 'comparison':
        return (
          <div className="p-6 h-full overflow-auto" style={{ background: 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' }}>
            <h2 className="text-xl font-bold text-purple-800 mb-4 text-center">{slide.title}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    {slide.headers.map((h, i) => (
                      <th key={i} className={`p-2 text-left ${i === 0 ? 'bg-gray-100' : i === 1 ? 'bg-pink-100 text-pink-700' : 'bg-blue-100 text-blue-700'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {slide.rows.map((row, i) => (
                    <tr key={i} className="border-b">
                      {row.map((cell, j) => (
                        <td key={j} className={`p-2 ${j === 0 ? 'font-medium' : ''}`}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'conclusion':
        return (
          <div className="p-8 h-full overflow-auto" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)' }}>
            <h2 className="text-3xl font-bold text-white mb-4 text-center">{slide.title}</h2>
            <p className="text-white/90 mb-4 text-center">{slide.content}</p>
            <div className="max-w-2xl mx-auto">
              <ul className="space-y-2">
                {slide.bullets.map((bullet, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-white">
                    <CheckCircle size={20} className="text-purple-200" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 p-4 bg-white/20 rounded-lg max-w-2xl mx-auto">
              <p className="text-white text-center">{slide.finalNote}</p>
            </div>
          </div>
        );

      default:
        return <div className="p-8 h-full flex items-center justify-center"><p className="text-gray-500">Contenu non disponible</p></div>;
    }
  };

  if (showCongrats) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-rose-600 flex items-center justify-center z-50">
        <div className="text-center p-8 max-w-2xl">
          <Award size={80} className="mx-auto text-yellow-400 animate-bounce mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">🎉 Félicitations ! 🎉</h1>
          <p className="text-2xl text-white/90 mb-4">Vous avez terminé l'Activité 11 avec succès !</p>
          <div className="bg-white/20 rounded-xl p-6 mb-6">
            <p className="text-3xl font-bold text-white mb-2">Score : {quizScore}/{quizQuestions.length}</p>
            <p className="text-white/80">
              {quizScore === 20 ? "Score parfait ! Vous êtes un(e) expert(e) de l'organisation en équipe !" :
               quizScore >= 18 ? "Excellent ! Maîtrise quasi-parfaite !" : "Très bon travail !"}
            </p>
          </div>
          <p className="text-lg text-white/80 mb-6">Vous maîtrisez maintenant l'organisation au sein d'une équipe pluri-professionnelle !</p>
          <div className="flex gap-4 justify-center">
            <button onClick={() => { setShowCongrats(false); setShowQuiz(false); setCurrentSlide(0); }} className="px-6 py-3 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors">Revoir le cours</button>
            <button onClick={() => setShowCongrats(false)} className="px-6 py-3 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-400 transition-colors">Voir mes réponses</button>
          </div>
        </div>
      </div>
    );
  }

  if (showQuiz) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-2xl font-bold text-purple-800 mb-6">Quiz - Activité 11</h2>
        <p className="text-gray-600 mb-6">Testez vos connaissances sur l'organisation en équipe pluri-professionnelle.</p>
        <div className="space-y-6">
          {quizQuestions.map((q, idx) => (
            <div key={q.id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              <p className="font-medium text-gray-800 mb-3">{idx + 1}. {q.question}</p>
              <div className="space-y-2">
                {q.options.map((option, optIdx) => (
                  <button key={optIdx} onClick={() => handleQuizAnswer(q.id, optIdx)} disabled={quizSubmitted}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      quizSubmitted ? optIdx === q.correct ? 'bg-green-100 border-green-500' : quizAnswers[q.id] === optIdx ? 'bg-red-100 border-red-500' : 'bg-gray-50 border-gray-200'
                      : quizAnswers[q.id] === optIdx ? 'bg-purple-100 border-purple-500' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                    <span className="flex items-center gap-2">
                      {quizSubmitted && optIdx === q.correct && <CheckCircle size={18} className="text-green-600" />}
                      {quizSubmitted && quizAnswers[q.id] === optIdx && optIdx !== q.correct && <XCircle size={18} className="text-red-600" />}
                      {option}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        {!quizSubmitted ? (
          <button onClick={submitQuiz} disabled={Object.keys(quizAnswers).length < quizQuestions.length}
            className="mt-6 w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
            Valider le quiz ({Object.keys(quizAnswers).length}/{quizQuestions.length} réponses)
          </button>
        ) : (
          <div className="mt-6 p-6 bg-purple-50 rounded-lg text-center">
            <h3 className="text-2xl font-bold text-purple-800">Score : {quizScore}/{quizQuestions.length}</h3>
            <p className="text-purple-600 mt-2">
              {quizScore >= 16 ? "Excellent ! Vous maîtrisez le sujet." : quizScore >= 12 ? "Bon travail ! Quelques points à revoir." : "Continuez à étudier pour améliorer vos connaissances."}
            </p>
            <button onClick={() => { setShowQuiz(false); setCurrentSlide(0); setQuizAnswers({}); setQuizSubmitted(false); }}
              className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">Revoir la présentation</button>
          </div>
        )}
      </div>
    );
  }

  const slide = slides[currentSlide];

  return (
    <div className="flex flex-col h-full">
      <div className="h-1 bg-gray-200"><div className="h-full bg-purple-500 transition-all duration-300" style={{ width: `${((currentSlide + 1) / slides.length) * 100}%` }} /></div>
      <div className="flex-1 overflow-hidden">{renderSlide(slide)}</div>
      <div className="flex items-center justify-between p-4 bg-white border-t">
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed">
          <ChevronLeft size={20} /> Précédent
        </button>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">{currentSlide + 1} / {slides.length}</span>
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors disabled:opacity-50">
            {isLoading ? (<><Loader2 size={18} className="animate-spin" /> Chargement...</>) : isPlaying ? (<><VolumeX size={18} /> Arrêter</>) : (<><Volume2 size={18} /> Écouter</>)}
          </button>
        </div>
        <button onClick={nextSlide} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
          {currentSlide === slides.length - 1 ? 'Quiz' : 'Suivant'} <ChevronRight size={20} />
        </button>
      </div>
    </div>
  );
};

export default DEAPActivite11Presentation;
